import { ColorMatrixFilter, Filter } from "pixi.js";
import { Finish } from "../../game/Materials";

/**
 * What a finish does to the wood under it.
 *
 * Oil doesn't sit on a board the way paint does — it soaks into the
 * fibers and stops the sanded surface scattering light, so the color
 * comes up, the wood deepens, and the gap between the early and late
 * growth widens. That is a change to wood already drawn rather than
 * anything drawn on top of it, which is why a finish is a filter over
 * the art layer instead of another layer inside it: every piece that
 * can carry a finish gets the look for free, whatever draws it.
 *
 * The numbers are pushed past life on purpose. The whole surface ladder
 * is built so each rung reads at a glance (see board.ts), and sanding
 * deliberately leaves the wood a shade paler than it was off the
 * planer — oiling is the rung that pays that back, and it is the one
 * the player stands and waits through.
 *
 * There is no sheen here, and there shouldn't be. The sun moves
 * (game/daylight.ts), so nothing baked into a sprite is allowed to
 * claim it knows where the light is.
 */
function oiledLook(): Filter {
  const filter = new ColorMatrixFilter();
  // Most of what the eye reads as "oiled" is depth, not color, so the
  // deepening carries the effect…
  filter.brightness(0.88, true);
  // …the growth rings pull away from the wood between them…
  filter.contrast(0.12, true);
  // …and the color comes up last, gently. A hard push here turns the
  // already-saturated species (cherry, purple heart) fluorescent.
  filter.saturate(0.22, true);
  // Rasterize with the render target rather than at a fixed size, for
  // the reason canvas-resolution.ts gives.
  filter.resolution = "inherit";
  return filter;
}

/**
 * One filter array per finish. Pixi filters hold no per-object state, so
 * a single array dresses every piece wearing that finish — the same
 * sharing TARGET_HIGHLIGHT_FILTERS relies on.
 */
export const FINISH_FILTERS: Record<Finish, Filter[]> = {
  mineralOil: [oiledLook()],
};
